import { TOKEN_RECOVERY_MAX_EXECUTIONS } from './tokenRecoveryConfig';

const TRIGGERS = ['wake', 'online', 'scheduled', 'retry', 'token_demand', 'manual'] as const;
const ROLES = ['leader', 'follower', 'delegated'] as const;
const REASONS = [
  'no_leader',
  'leader_timeout',
  'owner_busy',
  'no_token',
  'destroyed',
  'user_mismatch',
  'updates_disabled',
  'missing_lifetime',
  'unusable_lifetime',
  'stale_token',
  'shared_token_unusable',
  'owner_failure_unknown'
] as const;

export type TokenRecoveryTrigger = (typeof TRIGGERS)[number];
export type TokenRecoveryRole = (typeof ROLES)[number];
export type TokenRecoveryReason = (typeof REASONS)[number];

/** Safe, ephemeral metadata. Never include token contents or user identities. */
export interface TokenRecoveryDiagnostic {
  recoveryId: string;
  trigger: TokenRecoveryTrigger;
  role: TokenRecoveryRole;
  tabId: string;
  ownerTabId?: string;
  refreshAttemptCount: number;
  elapsedMs: number;
  ownerResponded?: boolean;
  ownerRequestSent?: boolean;
  tokenUsable?: boolean;
  reason?: TokenRecoveryReason;
  online?: boolean;
  visibility?: 'visible' | 'hidden';
}

export type TokenApplicationResult =
  | { applied: true }
  | { applied: false; reason: TokenRecoveryReason };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value && typeof value === 'object' && !Array.isArray(value));

const isTabId = (value: unknown): value is string =>
  typeof value === 'string' && /^tab_\d{1,16}_[a-z0-9]{1,20}$/.test(value);

const isElapsedMs = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 604_800_000;

export const sanitizeTokenRecoveryDiagnostic = (
  value: unknown
): TokenRecoveryDiagnostic | undefined => {
  if (
    !isRecord(value) ||
    typeof value.recoveryId !== 'string' ||
    !/^[a-f0-9]{32}$/.test(value.recoveryId) ||
    !TRIGGERS.includes(value.trigger as TokenRecoveryTrigger) ||
    !ROLES.includes(value.role as TokenRecoveryRole) ||
    !isTabId(value.tabId) ||
    !Number.isInteger(value.refreshAttemptCount) ||
    Number(value.refreshAttemptCount) < 0 ||
    Number(value.refreshAttemptCount) > TOKEN_RECOVERY_MAX_EXECUTIONS ||
    !isElapsedMs(value.elapsedMs)
  ) {
    return undefined;
  }

  return {
    recoveryId: value.recoveryId,
    trigger: value.trigger as TokenRecoveryTrigger,
    role: value.role as TokenRecoveryRole,
    tabId: value.tabId,
    refreshAttemptCount: Number(value.refreshAttemptCount),
    elapsedMs: Math.round(value.elapsedMs),
    ...(isTabId(value.ownerTabId) && { ownerTabId: value.ownerTabId }),
    ...(typeof value.ownerResponded === 'boolean' && { ownerResponded: value.ownerResponded }),
    ...(typeof value.ownerRequestSent === 'boolean' && {
      ownerRequestSent: value.ownerRequestSent
    }),
    ...(typeof value.tokenUsable === 'boolean' && { tokenUsable: value.tokenUsable }),
    ...(REASONS.includes(value.reason as TokenRecoveryReason) && {
      reason: value.reason as TokenRecoveryReason
    }),
    ...(typeof value.online === 'boolean' && { online: value.online }),
    ...((value.visibility === 'visible' || value.visibility === 'hidden') && {
      visibility: value.visibility
    })
  };
};

export const applyTokenRecoveryOwner = (
  requestRecovery: TokenRecoveryDiagnostic,
  ownerRecovery: TokenRecoveryDiagnostic
): void => {
  requestRecovery.recoveryId = ownerRecovery.recoveryId;
  requestRecovery.ownerTabId = ownerRecovery.ownerTabId;
  requestRecovery.ownerResponded = true;
  requestRecovery.refreshAttemptCount = ownerRecovery.refreshAttemptCount;
  requestRecovery.ownerRequestSent = ownerRecovery.ownerRequestSent;
};

export const createTokenRecoveryDiagnostic = (
  trigger: TokenRecoveryTrigger,
  tabId: string,
  ownerTabId: string | null,
  role: TokenRecoveryRole
): TokenRecoveryDiagnostic => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return {
    recoveryId: Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join(''),
    trigger,
    role,
    tabId,
    ...(ownerTabId && { ownerTabId }),
    refreshAttemptCount: 0,
    elapsedMs: 0,
    ownerResponded: role !== 'follower',
    ...(role !== 'follower' && { ownerRequestSent: false }),
    online: navigator.onLine,
    visibility: document.visibilityState === 'hidden' ? 'hidden' : 'visible'
  };
};
